"use client";
import { batchService } from "../../../../services/batchService/index.js";
import { isNullOrEmpty, instantiateObserver } from "../../../../utils/index.js";
import { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { getWHToDisplay, markAllWHAsUnavailable, getAvailableWHList, getUnavailableWHList, getWHListDetails, categorizeWH } from "../WarehouseDrawerUI/utils/warehouseDrawer.lib.js";
import { sortWarehouses } from "../../utils/warehouse.lib.js";
const useWarehouseStockStatus = ({
  warehouses,
  preferredWarehouse,
  locale,
  childItemNumber,
  batchSize,
  productFlags,
  configs,
  isLoading,
  pageName
}) => {
  var _a, _b;
  const initialCheckedState = !(pageName === "PDP" && (productFlags == null ? void 0 : productFlags.isShipToWarehouse) && (((_b = (_a = configs.featureConfigs) == null ? void 0 : _a.custom) == null ? void 0 : _b.bopiwOrStw_feature_on) ?? true));
  const [state, setState] = useState({
    warehouseList: [],
    isBatchLoading: false,
    isLoadingEnabledWH: false,
    isProductUnavailable: false,
    enabledWHList: [],
    disabledWHList: [],
    checked: initialCheckedState
  });
  const allWHList = useRef([]);
  const batchCount = useRef(0);
  const observerRef = useRef(null);
  const rootRef = useRef(null);
  const countryCode = locale.split("-")[1];
  const { enabledWHListLucid, enabledWHListBopiw, enabledWHListWio } = useMemo(
    () => {
      var _a2, _b2, _c, _d, _e, _f;
      return {
        enabledWHListLucid: new Set(
          ((_b2 = (_a2 = configs.enabledWarehouseListConfigs) == null ? void 0 : _a2.enabledWhListLucid) == null ? void 0 : _b2.custom.enabledWarehousesList[countryCode]) || []
        ),
        enabledWHListBopiw: new Set(
          ((_d = (_c = configs.enabledWarehouseListConfigs) == null ? void 0 : _c.enabledWhListBopiw) == null ? void 0 : _d.custom.enabledWarehousesList[countryCode]) || []
        ),
        enabledWHListWio: new Set(
          ((_f = (_e = configs.enabledWarehouseListConfigs) == null ? void 0 : _e.enabledWhListWio) == null ? void 0 : _f.custom.enabledWarehousesList[countryCode]) || []
        )
      };
    },
    [configs, countryCode]
  );
  const updateDisplayedWH = useCallback(
    async (allWHLists, disabledWarehouse) => {
      const sortedWarehouses = sortWarehouses(
        allWHLists,
        preferredWarehouse
      );
      const displayedList = await getWHToDisplay(
        sortedWarehouses,
        state.checked,
        preferredWarehouse
      );
      const lazyLoadedWHCount = !isNullOrEmpty(disabledWarehouse) ? 0 : batchCount.current;
      batchCount.current = lazyLoadedWHCount + batchSize;
      allWHList.current = sortedWarehouses;
      setState((prev) => ({ ...prev, warehouseList: displayedList }));
    },
    [batchSize, state.checked, preferredWarehouse]
  );
  const getWHStockAvailability = useCallback(
    async (fulfillmentCenters, disabledWarehouses = []) => {
      if (!(warehouses == null ? void 0 : warehouses.length)) return;
      if (!(fulfillmentCenters == null ? void 0 : fulfillmentCenters.length)) {
        const unavailableWHList = markAllWHAsUnavailable(warehouses);
        updateDisplayedWH(unavailableWHList, disabledWarehouses);
        return;
      }
      setState((prev) => ({ ...prev, isBatchLoading: true }));
      try {
        const batchResponse = await batchService(
          locale,
          [childItemNumber],
          fulfillmentCenters,
          "",
          configs.batchServiceConfig
        );
        const { inWarehouse, useWarehouseInventory } = batchResponse.batchInfo[0].programTypes;
        const availableWHList = getAvailableWHList(
          warehouses,
          fulfillmentCenters,
          inWarehouse,
          useWarehouseInventory
        );
        const updatedWarehouseList = !isNullOrEmpty(disabledWarehouses) ? [
          ...availableWHList,
          // Filters unavailable warehouses (disabled) from a list of warehouses
          // and returns an updated list to indicate out of stock and 'showWHSMolecule' false
          ...getUnavailableWHList(
            warehouses,
            disabledWarehouses
          )
        ] : [...allWHList.current, ...availableWHList];
        updateDisplayedWH(updatedWarehouseList, disabledWarehouses);
      } catch {
        setState((prev) => ({ ...prev, isProductUnavailable: false }));
        const unavailableWHList = markAllWHAsUnavailable(warehouses);
        updateDisplayedWH(unavailableWHList, disabledWarehouses);
      } finally {
        setState((prev) => ({ ...prev, isBatchLoading: false }));
      }
    },
    [
      configs.batchServiceConfig,
      childItemNumber,
      locale,
      warehouses,
      updateDisplayedWH
    ]
  );
  const getEnabledWarehouses = useCallback(async () => {
    var _a2;
    if (!(warehouses == null ? void 0 : warehouses.length)) {
      return;
    }
    setState((prev) => ({ ...prev, isLoadingEnabledWH: true }));
    try {
      const warehouseList = warehouses.map(
        (warehouse) => `${warehouse == null ? void 0 : warehouse.warehouseId}-wh`
      );
      const missingCriticalParams = !warehouseList.length || !configs.featureConfigs || !(enabledWHListLucid == null ? void 0 : enabledWHListLucid.size) || // For products that are both BOPIW and WIO, we need at least one list with entries
      ((productFlags == null ? void 0 : productFlags.isBopiwProduct) && (productFlags == null ? void 0 : productFlags.isWioProduct) ? !(enabledWHListBopiw == null ? void 0 : enabledWHListBopiw.size) && !(enabledWHListWio == null ? void 0 : enabledWHListWio.size) : (
        // For BOPIW-only or WIO-only products, we need that specific list with entries
        (productFlags == null ? void 0 : productFlags.isBopiwProduct) && !(enabledWHListBopiw == null ? void 0 : enabledWHListBopiw.size) || (productFlags == null ? void 0 : productFlags.isWioProduct) && !(enabledWHListWio == null ? void 0 : enabledWHListWio.size)
      ));
      if (missingCriticalParams) {
        throw new Error(
          "WarehouseDrawer: Missing required parameters for determining enabled warehouses. Check feature configs and enabled warehouse lists."
        );
      }
      const warehouseListDetails = getWHListDetails(
        warehouseList,
        configs.featureConfigs,
        enabledWHListLucid,
        enabledWHListBopiw,
        enabledWHListWio,
        productFlags
      );
      if (warehouseListDetails == null ? void 0 : warehouseListDetails.length) {
        const isStwEligible = Boolean(
          ((_a2 = configs.featureConfigs) == null ? void 0 : _a2.custom.bopiwOrStw_feature_on) && (productFlags == null ? void 0 : productFlags.isShipToWarehouse)
        );
        const { enabledWarehouses, disabledWarehouses } = categorizeWH(
          warehouseListDetails,
          isStwEligible
        );
        setState((prev) => ({
          ...prev,
          enabledWHList: enabledWarehouses,
          disabledWHList: disabledWarehouses
        }));
        getWHStockAvailability(
          enabledWarehouses.slice(0, batchSize),
          // send current batch
          disabledWarehouses
        );
      } else if (isNullOrEmpty(warehouseListDetails) && !state.checked) {
        const unavailableWHList = markAllWHAsUnavailable(warehouses);
        setState((prev) => ({
          ...prev,
          warehouseList: unavailableWHList,
          isProductUnavailable: true
        }));
      }
    } catch {
      setState((prev) => ({ ...prev, isProductUnavailable: true }));
    } finally {
      setState((prev) => ({ ...prev, isLoadingEnabledWH: false }));
    }
  }, [
    batchSize,
    state.checked,
    warehouses,
    getWHStockAvailability,
    enabledWHListLucid,
    enabledWHListBopiw,
    enabledWHListWio,
    productFlags,
    configs.featureConfigs
  ]);
  const loadWarehouseTiles = useCallback(async () => {
    if (state.isBatchLoading || !state.enabledWHList.length || !allWHList.current.length)
      return;
    const nextBatch = state.enabledWHList.slice(
      batchCount.current,
      batchCount.current + batchSize
    ) || [];
    if (!isNullOrEmpty(nextBatch)) {
      getWHStockAvailability(nextBatch);
    }
  }, [
    state.enabledWHList,
    state.isBatchLoading,
    getWHStockAvailability,
    batchSize
  ]);
  const sentinelRef = useCallback(
    (node) => {
      if (observerRef.current) observerRef.current.disconnect();
      if (isLoading || state.isBatchLoading || state.isLoadingEnabledWH)
        return;
      const handleIntersect = (entries) => {
        if (entries[0].isIntersecting) {
          loadWarehouseTiles();
        }
      };
      observerRef.current = instantiateObserver(handleIntersect, {
        root: rootRef.current,
        threshold: 0.35
      });
      if (node) observerRef.current.observe(node);
    },
    [
      isLoading,
      state.isBatchLoading,
      state.isLoadingEnabledWH,
      rootRef,
      loadWarehouseTiles
    ]
  );
  const showSentinel = useMemo(
    () => state.disabledWHList.length + state.enabledWHList.length !== allWHList.current.length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      state.disabledWHList.length,
      state.enabledWHList.length,
      allWHList.current.length
    ]
  );
  const resetStates = useCallback(() => {
    batchCount.current = 0;
    allWHList.current = [];
    setState((prev) => ({
      ...prev,
      warehouseList: [],
      enabledWHList: [],
      disabledWHList: [],
      isProductUnavailable: false
    }));
  }, []);
  const resetCheckboxState = useCallback(() => {
    setState((prev) => ({
      ...prev,
      checked: initialCheckedState
    }));
  }, [initialCheckedState]);
  const handleFilterCheckbox = useCallback(
    () => (event) => {
      setState((prev) => ({ ...prev, checked: event.target.checked }));
      event.target.removeAttribute("checked");
    },
    []
  );
  useEffect(() => {
    const getWarehousesToDisplay = async () => {
      var _a2;
      if (state.warehouseList.length > 0) {
        const displayedWH = await getWHToDisplay(
          allWHList == null ? void 0 : allWHList.current,
          state.checked,
          preferredWarehouse
        );
        setState((prev) => ({ ...prev, warehouseList: displayedWH }));
        (_a2 = rootRef == null ? void 0 : rootRef.current) == null ? void 0 : _a2.scroll({
          behavior: "smooth",
          top: 0
        });
      }
    };
    getWarehousesToDisplay();
  }, [state.checked]);
  useEffect(() => {
    if (!state.isLoadingEnabledWH) {
      resetStates();
      getEnabledWarehouses();
    }
  }, [warehouses, resetStates]);
  return {
    state,
    resetCheckboxState,
    showSentinel,
    sentinelRef,
    rootRef,
    allWHList,
    handleFilterCheckbox
  };
};
export {
  useWarehouseStockStatus
};
